import React, {useEffect, useState} from "react";
import {Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table} from "reactstrap";
import {useDispatch} from "react-redux";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import SweetAlert from "sweetalert2";
import RefundReturnReasonForm from "../../pending-return/view/content/refundReturnReasonForm";
import ButtonLoader from "../../../../share-components/ui/buttonLoader";
import {convertStoreCreditToRefund} from "../../store/orderSlice";

const STORE_CREDIT_STATUS_ID = 5;
const CONVERTIBLE_TYPES = ['Return', 'Manual'];

// Only store-credit rows from a customer return or an admin (manual) store credit can be converted.
export const isConvertibleStoreCredit = (row) =>
    !!row && Number(row.status_id) === STORE_CREDIT_STATUS_ID && CONVERTIBLE_TYPES.includes(row.type);

const schema = yup.object().shape({
    reason: yup.string().trim().required('You must enter a reason'),
});

const ConvertStoreCreditModal = (props) => {
    const {isOpen, row, rows, onClose, onConverted} = props;
    const dispatch = useDispatch();
    const [selectedIds, setSelectedIds] = useState([]);
    const [serverError, setServerError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const {handleSubmit, formState, reset, control, register, setValue, trigger, getValues} = useForm({
        mode: 'onChange',
        defaultValues: {reason: ''},
        resolver: yupResolver(schema),
    });
    const {errors} = formState;

    // Other store-credit rows of the same order on this page. One gift card can cover
    // several items, and the backend requires all of them to be converted together.
    const siblingRows = (rows || []).filter(r =>
        row && r.id !== row.id && r.orderName === row.orderName && isConvertibleStoreCredit(r)
    );

    useEffect(() => {
        if (isOpen && row) {
            setSelectedIds([row.id]);
            setServerError(null);
            setSubmitting(false);
            reset({reason: ''});
        }
    }, [isOpen, row]);

    const toggleSibling = (id, checked) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    };

    const close = () => {
        if (!submitting) {
            onClose();
        }
    };

    const onSubmit = (data) => {
        if (submitting) {
            return;
        }

        SweetAlert.fire({
            title: 'Void store credit and refund?',
            text: "The customer's store credit gift card will be permanently voided in Shopify and a refund will be issued to the original payment method. This cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, void and refund',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        }).then(result => {
            if (!result.isConfirmed) {
                return;
            }

            setSubmitting(true);
            setServerError(null);
            dispatch(convertStoreCreditToRefund({
                order_refund_ids: selectedIds,
                reason: data.reason.trim(),
            })).then(res => {
                setSubmitting(false);
                if (res.error) {
                    setServerError(res.payload || 'Failed to convert store credit to refund');
                    return;
                }
                onConverted();
            });
        });
    };

    if (!row) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} toggle={close} size="lg" backdrop={submitting ? 'static' : true}>
            <ModalHeader toggle={close}>Cancel Store Credit &rarr; Create Refund</ModalHeader>
            <form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                    <Alert color="warning">
                        The store credit gift card issued for {row.orderName} will be <strong>voided</strong> before the
                        refund is created. If the customer has already used any of it, the conversion will be refused.
                    </Alert>

                    <Table size="sm" bordered>
                        <thead>
                        <tr>
                            <th style={{width: '40px'}}></th>
                            <th>SKU</th>
                            <th>Type</th>
                            <th className="text-right">Store Credit (£)</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td><Input type="checkbox" className="ml-0 position-static" checked disabled/></td>
                            <td>{row.sku}</td>
                            <td>{row.type}</td>
                            <td className="text-right">{Number(row.total || 0).toFixed(2)}</td>
                        </tr>
                        {siblingRows.map(sibling => (
                            <tr key={sibling.id}>
                                <td>
                                    <Input type="checkbox" className="ml-0 position-static"
                                           disabled={submitting}
                                           checked={selectedIds.includes(sibling.id)}
                                           onChange={(e) => toggleSibling(sibling.id, e.target.checked)}/>
                                </td>
                                <td>{sibling.sku}</td>
                                <td>{sibling.type}</td>
                                <td className="text-right">{Number(sibling.total || 0).toFixed(2)}</td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                    {siblingRows.length > 0 &&
                        <FormGroup>
                            <Label className="text-muted small mb-0">
                                Tick other kept items that share the same gift card - they must be converted together.
                            </Label>
                        </FormGroup>
                    }

                    <RefundReturnReasonForm control={control} errors={errors} register={register}
                                            setValue={setValue} trigger={trigger} getValues={getValues}/>

                    {serverError &&
                        <Alert color="danger" className="mb-0">{serverError}</Alert>
                    }
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" type="button" onClick={close} disabled={submitting}>Close</Button>
                    <ButtonLoader color="danger" type="submit" disabled={submitting} loading={submitting}
                                  btntext="Void Store Credit & Refund"/>
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default ConvertStoreCreditModal;
